import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Quote as QuoteIcon, Plus, Trash2, Pencil, Sparkles, Heart, Share2, X, Check } from "lucide-react";

const EMPTY = { author: "", text: "", theme: "", source: "", image_url: "", bio: "", why: "",
  status: "published", publish_date: "", book_ids: [], media: [] };

export default function AdminQuotes() {
  const [data, setData] = useState({ quotes: [], top_shared: [], top_liked: [] });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/quotes").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.author.trim() || !form.text.trim()) return toast.error("Auteur et citation requis.");
    setBusy(true);
    try {
      const payload = { ...form, book_ids: form.book_ids || [], media: form.media || [],
        publish_date: form.publish_date || null };
      if (form.quote_id) await api.put(`/admin/quotes/${form.quote_id}`, payload);
      else await api.post("/admin/quotes", payload);
      toast.success("Citation enregistrée."); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => { try { await api.delete(`/admin/quotes/${id}`); toast.success("Supprimée."); load(); } catch (e) { toast.error(apiErr(e)); } };

  const aiSuggest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/quotes/ai-suggest"); toast.success(`${data.count} citation(s) proposée(s) en brouillon — à valider.`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-quotes">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><QuoteIcon size={18} className="text-gold" /><h3 className="font-display text-lg">Citation du jour</h3></div>
        <div className="flex gap-2">
          <button data-testid="quote-ai-suggest" onClick={aiSuggest} disabled={busy} className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-gold/40 hover:text-gold disabled:opacity-50"><Sparkles size={15} /> Suggérer par IA</button>
          <button data-testid="quote-new" onClick={() => setForm({ ...EMPTY })} className="inline-flex items-center gap-2 gold-gradient text-black rounded-full px-4 py-2 text-sm font-medium"><Plus size={15} /> Nouvelle citation</button>
        </div>
      </div>

      {(data.top_shared?.length > 0 || data.top_liked?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div className="card-surface p-4">
            <p className="text-white/50 text-xs mb-2 flex items-center gap-1.5"><Share2 size={13} /> Les plus partagées</p>
            {data.top_shared.slice(0, 3).map((q) => <p key={q.quote_id} className="text-white/75 text-sm truncate">· {q.author} <span className="text-gold">({q.shares || 0})</span></p>)}
          </div>
          <div className="card-surface p-4">
            <p className="text-white/50 text-xs mb-2 flex items-center gap-1.5"><Heart size={13} /> Les plus appréciées</p>
            {data.top_liked.slice(0, 3).map((q) => <p key={q.quote_id} className="text-white/75 text-sm truncate">· {q.author} <span className="text-gold">({q.likes || 0})</span></p>)}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {data.quotes.map((q) => (
          <div key={q.quote_id} className="card-surface p-4 flex items-start gap-3" data-testid={`admin-quote-${q.quote_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{q.author}</span>
                {q.status === "draft" && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">Brouillon{q.ai_suggested ? " · IA" : ""}</span>}
                {q.publish_date && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">📅 {q.publish_date}</span>}
                <span className="text-white/30 text-xs">❤️ {q.likes || 0} · 📤 {q.shares || 0}</span>
              </div>
              <p className="text-white/60 text-sm mt-1 line-clamp-2">« {q.text} »</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`quote-edit-${q.quote_id}`} onClick={() => setForm({ ...EMPTY, ...q, publish_date: q.publish_date || "" })} className="text-white/50 hover:text-gold"><Pencil size={15} /></button>
              <button data-testid={`quote-del-${q.quote_id}`} onClick={() => remove(q.quote_id)} className="text-red-300"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }} onClick={() => setForm(null)}>
          <div className="w-full max-w-lg card-surface p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quote-form">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-lg">{form.quote_id ? "Modifier" : "Nouvelle"} citation</h3><button onClick={() => setForm(null)}><X size={20} className="text-white/40" /></button></div>
            <div className="space-y-3">
              <input data-testid="qf-author" value={form.author} onChange={(e) => set("author", e.target.value)} placeholder="Auteur *" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <textarea data-testid="qf-text" rows={2} value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Citation *" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="qf-theme" value={form.theme} onChange={(e) => set("theme", e.target.value)} placeholder="Thème" className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
                <input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Source" className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              </div>
              <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="URL de la photo/portrait (optionnel)" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Courte biographie" rows={2} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
              <textarea value={form.why} onChange={(e) => set("why", e.target.value)} placeholder="Pourquoi cette citation est importante ?" rows={2} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/40 mb-1">Statut</label>
                  <select data-testid="qf-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm">
                    <option value="published" className="bg-[#0a0a0a]">Publiée</option>
                    <option value="draft" className="bg-[#0a0a0a]">Brouillon</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">Programmer (date)</label>
                  <input data-testid="qf-date" type="date" value={form.publish_date} onChange={(e) => set("publish_date", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm" />
                </div>
              </div>
              <button data-testid="qf-save" onClick={save} disabled={busy} className="w-full gold-gradient text-black rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2"><Check size={15} /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
